type UnknownRecord = Record<string, unknown>;

const ATTR_MIRROR_KEYS = new Set([
  'sexualOrientation',
  'transgender',
  'deceased',
  'isDeceased',
  'birthDate',
  'deathDate',
  'pregnancy',
  'stillbirth',
  'location',
  'culturalHeritageTags',
  'medicalConditionTags',
  'notes',
  'photoRef',
  'tags',
]);

const TAG_FIELDS = new Set(['culturalHeritageTags', 'medicalConditionTags', 'tags']);
const DATE_FIELD_KEYS = new Set(['birthDate', 'deathDate']);
const NUMERIC_FIELD_KEYS = new Set(['birthYear', 'deathYear', 'age']);

const PROPS_SKIP_KEYS = new Set([
  'attrs',
  'props',
  'transgender',
  'deceased',
  'pregnancy',
  'stillbirth',
  'name',
  'gender',
  'genderIdentity',
  'notes',
  'photoRef',
  'location',
  'sexualOrientation',
  'indexPerson',
  'institution',
  'pet',
  'birthDate',
  'deathDate',
  'showDeceased',
  'isDeceased',
  'culturalHeritageTags',
  'medicalConditionTags',
  'tags',
  'culture',
  'immigration'
]);

const coerceBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  }
  if (typeof value === 'object' && value !== null && typeof (value as any).valueOf === 'function') {
    return coerceBoolean((value as any).valueOf());
  }
  return undefined;
};

const uniqueStrings = (values: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    if (!seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  }
  return result;
};

export const parseTagList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return uniqueStrings(
      value
        .map((entry) => (typeof entry === 'string' ? entry : entry != null ? String(entry) : ''))
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0),
    );
  }

  if (typeof value === 'string') {
    return uniqueStrings(
      value
        .split(',')
        .map((token) => token.trim())
        .filter((token) => token.length > 0),
    );
  }

  if (typeof value === 'object' && value !== null && Symbol.iterator in value) {
    return parseTagList(Array.from(value as Iterable<unknown>));
  }

  return [];
};

const sanitizeString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  return String(value);
};

const normalizeDateString = (value: unknown): string | undefined => {
  if (value == null) return undefined;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return undefined;
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) return undefined;
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const isoCandidate = trimmed.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(isoCandidate)) return isoCandidate;
    const parsed = Date.parse(trimmed);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toISOString().slice(0, 10);
    }
    return trimmed;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().slice(0, 10);
    }
  }

  return undefined;
};

const coerceNumericString = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) return undefined;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
  }
  return undefined;
};

export const normalizePersonUpdates = (incomingUpdates: Record<string, any>): Record<string, any> => {
  const next: Record<string, any> = { ...incomingUpdates };
  const incomingAttrs: UnknownRecord =
    typeof incomingUpdates.attrs === 'object' && incomingUpdates.attrs !== null
      ? { ...(incomingUpdates.attrs as UnknownRecord) }
      : {};
  const attrPayload: UnknownRecord = { ...incomingAttrs };

  if ('transgender' in incomingUpdates) {
    const bool = coerceBoolean(incomingUpdates.transgender) ?? false;
    next.transgender = bool;
    attrPayload.transgender = bool;
    attrPayload.genderIdentity = bool ? 'transgender' : undefined;
  }

  if ('deceased' in incomingUpdates) {
    const bool = coerceBoolean(incomingUpdates.deceased) ?? false;
    next.deceased = bool;
    attrPayload.deceased = bool;
    attrPayload.isDeceased = bool;
  }

  if ('pregnancy' in incomingUpdates) {
    const bool = coerceBoolean(incomingUpdates.pregnancy) ?? false;
    next.pregnancy = bool;
    attrPayload.pregnancy = bool;
  }

  if ('stillbirth' in incomingUpdates) {
    const bool = coerceBoolean(incomingUpdates.stillbirth) ?? false;
    next.stillbirth = bool;
    attrPayload.stillbirth = bool;
  }

  TAG_FIELDS.forEach((key) => {
    if (key in incomingUpdates) {
      const list = parseTagList(incomingUpdates[key]);
      next[key] = list;
      attrPayload[key] = list;
    }
  });
  
  // Generic: mirror any array-of-string lists to attrs as well
  Object.entries(incomingUpdates).forEach(([key, val]) => {
    if (key === 'attrs') return;
    if (Array.isArray(val)) {
      const list = parseTagList(val);
      if (list.length > 0) {
        next[key] = list;
        (attrPayload as any)[key] = list;
      } else if (val.length === 0) {
        next[key] = [];
        (attrPayload as any)[key] = [];
      }
    }
  });

  let propsPayload: UnknownRecord | undefined =
    typeof incomingUpdates.props === 'object' && incomingUpdates.props !== null
      ? { ...(incomingUpdates.props as UnknownRecord) }
      : undefined;

  const ensurePropsPayload = () => {
    if (!propsPayload) propsPayload = {};
    return propsPayload;
  };

  if ('name' in incomingUpdates) {
    const nameValue = sanitizeString(incomingUpdates.name);
    next.name = nameValue;
    const target = ensurePropsPayload();
    target.name = nameValue;
  }

  if ('gender' in incomingUpdates) {
    const genderValue = sanitizeString(incomingUpdates.gender);
    next.gender = genderValue;
    const trimmed = genderValue.trim();
    const target = ensurePropsPayload();
    target.gender = genderValue;
    if (trimmed.length > 0) {
      target.genderIdentity = trimmed;
      attrPayload.genderIdentity = trimmed;
    } else {
      target.genderIdentity = undefined;
      attrPayload.genderIdentity = undefined;
    }
  }

  if ('genderIdentity' in incomingUpdates) {
    const genderIdentityValue = sanitizeString(incomingUpdates.genderIdentity);
    const trimmed = genderIdentityValue.trim();
    const normalized = trimmed.length > 0 ? trimmed : undefined;
    const target = ensurePropsPayload();
    target.genderIdentity = normalized;
    attrPayload.genderIdentity = normalized;
    if (normalized !== undefined) {
      next.genderIdentity = normalized;
    } else {
      delete next.genderIdentity;
    }
  }

  DATE_FIELD_KEYS.forEach((key) => {
    if (key in incomingUpdates) {
      const normalized = normalizeDateString(incomingUpdates[key]);
      const target = ensurePropsPayload();
      if (normalized === undefined) {
        next[key] = undefined;
        target[key] = undefined;
        attrPayload[key] = undefined;
      } else {
        next[key] = normalized;
        target[key] = normalized;
        attrPayload[key] = normalized;
      }
    }
  });

  if ('notes' in incomingUpdates) {
    const notes = sanitizeString(incomingUpdates.notes);
    next.notes = notes;
    attrPayload.notes = notes;
  }

  if ('photoRef' in incomingUpdates) {
    const photoRef = sanitizeString(incomingUpdates.photoRef).trim();
    next.photoRef = photoRef;
    attrPayload.photoRef = photoRef;
  }

  if ('location' in incomingUpdates) {
    attrPayload.location = sanitizeString(incomingUpdates.location);
  }

  const shouldMirrorToProps = (key: string, value: unknown): boolean => {
    if (PROPS_SKIP_KEYS.has(key)) return false;
    if (TAG_FIELDS.has(key)) return false;
    if (DATE_FIELD_KEYS.has(key)) return false;
    if (key === 'attrs' || key === 'props') return false;
    if (value && typeof value === 'object') return false;
    return true;
  };

  Object.entries(incomingUpdates).forEach(([key, value]) => {
    if (!shouldMirrorToProps(key, value)) {
      return;
    }
    const target = ensurePropsPayload();

    if (value === undefined || value === null) {
      delete target[key];
      return;
    }

    if (NUMERIC_FIELD_KEYS.has(key)) {
      const numericValue = coerceNumericString(value);
      if (numericValue === undefined) {
        delete target[key];
        if (typeof value === 'string') {
          next[key] = value;
        } else {
          delete next[key];
        }
        return;
      }
      target[key] = numericValue;
      next[key] = numericValue;
      return;
    }

    if (typeof value === 'boolean') {
      target[key] = value;
      next[key] = value;
      return;
    }

    const sanitized = sanitizeString(value);
    target[key] = sanitized;
    next[key] = sanitized;
  });

  ATTR_MIRROR_KEYS.forEach((key) => {
    if (!(key in attrPayload) || !(key in next)) return;
    if (attrPayload[key] === next[key]) return;
    attrPayload[key] = attrPayload[key] ?? next[key];
  });

  if (Object.keys(attrPayload).length > 0) {
    next.attrs = attrPayload;
  } else {
    delete next.attrs;
  }

  if (propsPayload && Object.keys(propsPayload).length > 0) {
    next.props = propsPayload;
  } else if (propsPayload) {
    next.props = propsPayload;
  } else {
    delete next.props;
  }

  return next;
};

export const resolvePersonFieldFromAttrs = (
  person: UnknownRecord,
  key: string,
  attrs: UnknownRecord,
): unknown => {
  const props =
    typeof person.props === 'object' && person.props !== null ? (person.props as UnknownRecord) : {};

  if (TAG_FIELDS.has(key)) {
    const topLevel = person[key];
    const attrLevel = attrs[key];
    const topList = Array.isArray(topLevel) ? parseTagList(topLevel) : [];
    const attrList = Array.isArray(attrLevel) ? parseTagList(attrLevel) : [];

    if (topList.length > 0) return topList;
    if (attrList.length > 0) return attrList;
    return undefined;
  }

  // Generic fallback: if either level has an array-like list, surface it
  const topMaybeList = person[key];
  if (Array.isArray(topMaybeList)) {
    return parseTagList(topMaybeList);
  }
  const attrMaybeList = attrs[key];
  if (Array.isArray(attrMaybeList)) {
    return parseTagList(attrMaybeList);
  }

  if (key === 'notes' || key === 'photoRef') {
    const top = person[key];
    if (typeof top === 'string') return top;
    const attr = attrs[key];
    if (typeof attr === 'string') return attr;
    return undefined;
  }

  if (DATE_FIELD_KEYS.has(key)) {
    const propValue = props[key];
    const normalizedProp = normalizeDateString(propValue);
    if (normalizedProp) return normalizedProp;

    const top = normalizeDateString(person[key]);
    if (top) return top;

    const attr = normalizeDateString(attrs[key]);
    if (attr) return attr;
    return undefined;
  }

  return undefined;
};
